import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { drivers } from "../services/api";
import LoadingSpinner from "../components/LoadingSpinner/LoadingSpinner";
import moment from "moment-timezone";
import {
  FaUser,
  FaIdCard,
  FaCalendarAlt,
  FaPhoneAlt,
  FaEnvelope,
  FaCarAlt,
  FaMapMarkerAlt,
  FaCheck,
  FaExchangeAlt,
  FaRoute,
  FaClock,
  FaClipboardList,
  FaPencilAlt,
  FaSignOutAlt,
  FaCarSide,
  FaBus,
  FaSuitcase,
  FaUserFriends,
  FaCommentAlt,
  FaExclamationTriangle,
  FaSyncAlt,
  FaBell,
  FaCheckCircle,
  FaEye,
} from "react-icons/fa";
import { RxCross2 } from "react-icons/rx";
import { FaVanShuttle, FaBusSimple } from "react-icons/fa6";
import Swal from "sweetalert2";
import Header from "../components/Header/Header";
import Footer from "../components/Footer/Footer";

// Driver Notification Component
const DriverNotification = ({ transfers, refreshData }) => {
  const [showNotifications, setShowNotifications] = useState(false);
  const [newAssignments, setNewAssignments] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const notificationRef = useRef(null);

  useEffect(() => {
    // Get stored read notifications from localStorage
    const readNotifications =
      JSON.parse(localStorage.getItem("readDriverNotifications")) || [];

    // Filter transfers to find new assignments (created within last 24 hours)
    const newlyAssigned = transfers.filter((transfer) => {
      const isRecent =
        (new Date() - new Date(transfer.createdAt)) / (1000 * 60 * 60) < 24;
      const isUnread = !readNotifications.includes(transfer._id);
      return isRecent && isUnread && transfer.status === "confirmed";
    });

    setNewAssignments(newlyAssigned);
    setNotificationCount(newlyAssigned.length);
  }, [transfers]);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setShowNotifications(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const markAsRead = (transferId) => {
    // Get current read notifications
    const readNotifications =
      JSON.parse(localStorage.getItem("readDriverNotifications")) || [];

    // Add this transfer ID if not already included
    if (!readNotifications.includes(transferId)) {
      readNotifications.push(transferId);
      localStorage.setItem(
        "readDriverNotifications",
        JSON.stringify(readNotifications)
      );

      // Update state
      setNewAssignments((prev) => prev.filter((t) => t._id !== transferId));
      setNotificationCount((prev) => prev - 1);
    }
  };

  const markAllAsRead = () => {
    // Get all unread transfer IDs
    const transferIds = newAssignments.map((transfer) => transfer._id);

    // Get current read notifications
    const readNotifications =
      JSON.parse(localStorage.getItem("readDriverNotifications")) || [];

    // Add all new transfers
    const updatedReadNotifications = [
      ...new Set([...readNotifications, ...transferIds]),
    ];
    localStorage.setItem(
      "readDriverNotifications",
      JSON.stringify(updatedReadNotifications)
    );

    // Update state
    setNewAssignments([]);
    setNotificationCount(0);
    setShowNotifications(false);
  };

  const getVehicleTypeIcon = (type) => {
    switch (type) {
      case "car":
        return <FaCarSide className="w-4 h-4" />;
      case "minivan":
        return <FaVanShuttle className="w-4 h-4" />;
      case "van":
        return <FaBusSimple className="w-4 h-4" />;
      case "minibus":
        return <FaBus className="w-4 h-4" />;
      default:
        return <FaCarAlt className="w-4 h-4" />;
    }
  };

  return (
    <div className="relative" ref={notificationRef}>
      <button
        onClick={() => setShowNotifications(!showNotifications)}
        className="relative p-2 text-gray-600 hover:text-blue-600 focus:outline-none"
        aria-label="Notifications"
      >
        <FaBell className="w-6 h-6" />
        {notificationCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full">
            {notificationCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {showNotifications && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 w-80 mt-2 bg-white rounded-lg shadow-lg overflow-hidden z-20"
          >
            <div className="p-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
              <h3 className="text-sm font-semibold text-gray-700">
                Notifications
              </h3>
              {notificationCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
                >
                  <FaCheckCircle className="mr-1" /> Mark all as read
                </button>
              )}
            </div>

            <div className="max-h-96 overflow-y-auto">
              {newAssignments.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {newAssignments.map((transfer) => (
                    <div
                      key={transfer._id}
                      className="p-3 hover:bg-blue-50 transition-colors"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center text-sm font-medium text-blue-700">
                            <FaExchangeAlt className="mr-1 text-blue-600" />
                            New Transfer Assigned
                          </div>
                          <p className="text-xs text-gray-500">
                            {moment(transfer.createdAt).fromNow()}
                          </p>
                        </div>
                        <button
                          onClick={() => markAsRead(transfer._id)}
                          className="text-gray-400 hover:text-blue-600"
                          title="Mark as read"
                        >
                          <RxCross2 className="w-4 h-4" />
                        </button>
                      </div>{" "}
                      <div className="mt-2 text-sm">
                        <p className="font-medium">
                          {transfer.pickupLocation} → {transfer.dropoffLocation}
                        </p>
                        <p className="text-gray-600">
                          {moment(transfer.pickUpDate).format("MMM D, YYYY")} at{" "}
                          {transfer.pickupHours}:{transfer.pickupMinutes}
                        </p>
                        <div className="flex items-center mt-1 text-xs text-gray-500">
                          {getVehicleTypeIcon(transfer.carType)}
                          <span className="ml-1 capitalize">
                            {transfer.carType}
                          </span>
                          <span className="mx-1">•</span>
                          <FaUserFriends className="mr-1" /> {transfer.persons}{" "}
                          persons
                        </div>{" "}
                        <div className="mt-2 flex space-x-2">
                          <a
                            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                              transfer.pickupLocation + ", Athens, Greece"
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center px-3 py-1.5 bg-green-100 text-green-700 rounded-md text-xs font-medium hover:bg-green-200 transition-colors w-full"
                          >
                            <FaMapMarkerAlt className="mr-1 w-3 h-3" />
                            Pickup Directions
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 px-4 text-center text-gray-500">
                  <FaBell className="mx-auto h-8 w-8 text-gray-300 mb-2" />
                  <p className="text-sm">No new notifications</p>
                  <p className="text-xs text-gray-400 mt-1">
                    New transfer assignments will appear here
                  </p>
                </div>
              )}
            </div>

            <div className="p-2 border-t border-gray-200 bg-gray-50 flex justify-center">
              <button
                onClick={() => {
                  setShowNotifications(false);
                  refreshData();
                }}
                className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
              >
                <FaSyncAlt className="mr-1 w-3 h-3" /> Refresh notifications
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const DriverDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profile, setProfile] = useState(null);
  const [transfers, setTransfers] = useState([]);
  const [activeTab, setActiveTab] = useState("upcoming");
  const [refreshing, setRefreshing] = useState(false);
  useEffect(() => {
    verifyAuth();
    fetchData();

    // Set up polling to check for new transfers every 2 minutes
    const pollingInterval = setInterval(() => {
      fetchData();
    }, 120000); // 120000 ms = 2 minutes

    // Clean up interval on component unmount
    return () => clearInterval(pollingInterval);
  }, []);

  const verifyAuth = async () => {
    try {
      const token = localStorage.getItem("driverToken");
      if (!token) {
        throw new Error("No authentication token found");
      }
    } catch (error) {
      console.error("Authentication verification failed:", error);
      localStorage.removeItem("driverToken");
      localStorage.removeItem("driverData");
      navigate("/driver/login");
    }
  };

  const fetchData = async () => {
    try {
      setRefreshing(true);
      const [profileRes, transfersRes] = await Promise.all([
        drivers.getProfile(),
        drivers.getAssignedTransfers(),
      ]);

      setProfile(profileRes.data);
      setTransfers(transfersRes.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching driver data:", err);
      setError("Failed to load your data. Please try again.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("driverToken");
    localStorage.removeItem("driverData");
    navigate("/driver/login");
  };

  const handleCompleteTransfer = async (transferId) => {
    try {
      const result = await Swal.fire({
        title: "Complete Transfer",
        text: "Are you sure you want to mark this transfer as completed?",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, complete it",
      });

      if (result.isConfirmed) {
        await drivers.completeTransfer(transferId);
        await fetchData();

        await Swal.fire(
          "Completed!",
          "The transfer has been marked as completed.",
          "success"
        );
      }
    } catch (error) {
      console.error("Error completing transfer:", error);
      Swal.fire(
        "Error",
        "Failed to complete the transfer. Please try again.",
        "error"
      );
    }
  };

  const handleAddNotes = async (transferId, currentNotes = "") => {
    try {
      const { value: notes } = await Swal.fire({
        title: "Add Notes",
        input: "textarea",
        inputLabel: "Notes for this transfer",
        inputValue: currentNotes,
        inputPlaceholder: "Enter any information about this transfer...",
        showCancelButton: true,
        inputValidator: (value) => {
          if (!value) {
            return "Please enter some notes";
          }
        },
      });

      if (notes) {
        await drivers.addTransferNotes(transferId, notes);
        await fetchData();

        await Swal.fire("Saved!", "Your notes have been saved.", "success");
      }
    } catch (error) {
      console.error("Error adding notes:", error);
      Swal.fire("Error", "Failed to save notes. Please try again.", "error");
    }
  };

  const filterTransfers = () => {
    const now = new Date();

    if (activeTab === "upcoming") {
      return transfers
        .filter(
          (t) => t.status === "confirmed" && new Date(t.pickUpDate) >= now
        )
        .sort((a, b) => new Date(a.pickUpDate) - new Date(b.pickUpDate));
    } else if (activeTab === "completed") {
      return transfers
        .filter((t) => t.status === "completed")
        .sort((a, b) => new Date(b.pickUpDate) - new Date(a.pickUpDate));
    } else if (activeTab === "today") {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      return transfers
        .filter((t) => {
          const pickupDate = new Date(t.pickUpDate);
          pickupDate.setHours(0, 0, 0, 0);
          return (
            pickupDate.getTime() === today.getTime() && t.status === "confirmed"
          );
        })
        .sort((a, b) => {
          // Sort by time
          const timeA =
            parseInt(a.pickupHours) * 60 + parseInt(a.pickupMinutes);
          const timeB =
            parseInt(b.pickupHours) * 60 + parseInt(b.pickupMinutes);
          return timeA - timeB;
        });
    }

    return transfers;
  };

  const filteredTransfers = filterTransfers();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const getVehicleTypeIcon = (type) => {
    switch (type) {
      case "car":
        return <FaCarSide className="w-4 h-4" />;
      case "minivan":
        return <FaVanShuttle className="w-4 h-4" />;
      case "van":
        return <FaBusSimple className="w-4 h-4" />;
      case "minibus":
        return <FaBus className="w-4 h-4" />;
      default:
        return <FaCarAlt className="w-4 h-4" />;
    }
  };

  return (
    <div className="bg-gradient-to-l from-gray-600 via-gray-400 to-slate-700 min-h-screen">
      <Header />
      <div className="bg-gradient-to-b from-gray-600 via-gray-400 to-slate-700 h-full w-full">
        <div className="bg-white border-b shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-3">
              <h2 className="text-lg font-medium text-gray-900">
                Driver Dashboard
              </h2>
              <div className="flex items-center space-x-4">
                <button
                  onClick={fetchData}
                  className={`text-sm text-blue-600 hover:text-blue-800 flex items-center ${
                    refreshing ? "opacity-50" : ""
                  }`}
                  disabled={refreshing}
                >
                  <FaSyncAlt
                    className={`mr-1 w-4 h-4 ${
                      refreshing ? "animate-spin" : ""
                    }`}
                  />
                  {refreshing ? "Refreshing..." : "Refresh"}
                </button>
                <DriverNotification
                  transfers={transfers}
                  refreshData={fetchData}
                />
                <button
                  onClick={handleLogout}
                  className="text-sm text-gray-500 hover:text-gray-700 flex items-center"
                >
                  <FaSignOutAlt className="mr-1 w-4 h-4" />
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
        <main className="max-w-7xl min-h-screen mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-transparent">
          {error && (
            <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <FaExclamationTriangle className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 h-full">
            {/* Driver Profile */}
            <div className="lg:col-span-1">
              <div className="bg-gradient-to-b from-gray-300 via-white to-slate-400 shadow rounded-lg overflow-hidden">
                <div className="bg-slate-400 px-4 py-5 sm:px-6 text-center">
                  <div className="mx-auto w-24 h-24 rounded-full bg-gradient-to-b from-gray-600 via-gray-400 to-slate-700 p-2 mb-4">
                    {profile?.avatar ? (
                      <img
                        src={profile.avatar}
                        alt={`${profile.name} ${profile.surname}`}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full rounded-full bg-slate-100 flex items-center justify-center">
                        <FaUser className="text-slate-600 text-4xl" />
                      </div>
                    )}
                  </div>
                  <h2 className="text-xl font-bold text-white">
                    {profile?.name} {profile?.surname}
                  </h2>
                  <p className="text-blue-100 mt-1">Professional Driver</p>
                </div>

                <div className="px-4 py-5 sm:p-6 space-y-6">
                  <div className="flex items-center text-gray-700">
                    <FaEnvelope className="w-5 h-5 mr-3 text-gray-500" />
                    <span className="text-sm">{profile?.email}</span>
                  </div>

                  <div className="flex items-center text-gray-700">
                    <FaPhoneAlt className="w-5 h-5 mr-3 text-gray-500" />
                    <span className="text-sm">{profile?.phone}</span>
                  </div>

                  <div className="flex items-center text-gray-700">
                    <FaIdCard className="w-5 h-5 mr-3 text-gray-500" />
                    <span className="text-sm">
                      License: {profile?.licenseNumber}
                    </span>
                  </div>

                  <div className="flex items-center text-gray-700">
                    <FaCalendarAlt className="w-5 h-5 mr-3 text-gray-500" />
                    <span className="text-sm">
                      Expires:{" "}
                      {moment(profile?.licenseExpiry).format("DD/MM/YYYY")}
                    </span>
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <h3 className="text-sm font-medium text-gray-700 mb-3">
                      Vehicle Qualifications
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {profile?.vehicleTypes?.map((type) => (
                        <span
                          key={type}
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {getVehicleTypeIcon(type)}
                          <span className="ml-1 capitalize">{type}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 bg-gradient-to-b from-gray-300 via-gray-200 to-slate-400 shadow rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Transfer Statistics
                  </h3>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-blue-600">
                      {transfers.filter((t) => t.status === "confirmed").length}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">Upcoming</p>
                  </div>

                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-green-600">
                      {transfers.filter((t) => t.status === "completed").length}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">Completed</p>
                  </div>

                  <div className="bg-yellow-50 rounded-lg p-4 text-center col-span-2">
                    <p className="text-2xl font-bold text-yellow-600">
                      {
                        transfers.filter((t) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const pickupDate = new Date(t.pickUpDate);
                          pickupDate.setHours(0, 0, 0, 0);
                          return (
                            pickupDate.getTime() === today.getTime() &&
                            t.status === "confirmed"
                          );
                        }).length
                      }
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      Today's Transfers
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Transfers Section */}
            <div className="lg:col-span-3">
              <div className="bg-gradient-to-b from-gray-300 via-gray-200 to-slate-400 shadow rounded-lg overflow-hidden">
                <div className="px-4 py-5 sm:px-6 flex flex-wrap justify-between items-center">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    My Transfers
                  </h3>

                  <button
                    onClick={fetchData}
                    disabled={refreshing}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none transition-colors duration-150"
                  >
                    <FaSyncAlt
                      className={`mr-2 ${refreshing ? "animate-spin" : ""}`}
                    />
                    {refreshing ? "Refreshing..." : "Refresh"}
                  </button>
                </div>

                <div className="border-b border-gray-200">
                  <nav className="-mb-px flex" aria-label="Tabs">
                    <button
                      onClick={() => setActiveTab("today")}
                      className={`
                      py-4 px-1 mx-4 border-b-2 font-medium text-sm
                      ${
                        activeTab === "today"
                          ? "border-blue-500 text-blue-600"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }
                    `}
                    >
                      <FaCalendarAlt className="inline-block mr-2" />
                      Today's Transfers
                    </button>

                    <button
                      onClick={() => setActiveTab("upcoming")}
                      className={`
                      py-4 px-1 mx-4 border-b-2 font-medium text-sm
                      ${
                        activeTab === "upcoming"
                          ? "border-blue-500 text-blue-600"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }
                    `}
                    >
                      <FaRoute className="inline-block mr-2" />
                      Upcoming
                    </button>

                    <button
                      onClick={() => setActiveTab("completed")}
                      className={`
                      py-4 px-1 mx-4 border-b-2 font-medium text-sm
                      ${
                        activeTab === "completed"
                          ? "border-blue-500 text-blue-600"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }
                    `}
                    >
                      <FaCheck className="inline-block mr-2" />
                      Completed
                    </button>
                  </nav>
                </div>

                <div className="divide-y divide-gray-200">
                  {filteredTransfers.length === 0 ? (
                    <div className="px-4 py-12 text-center">
                      <div className="text-gray-500 mb-4">
                        {activeTab === "today" ? (
                          <FaCalendarAlt className="mx-auto h-10 w-10" />
                        ) : activeTab === "upcoming" ? (
                          <FaRoute className="mx-auto h-10 w-10" />
                        ) : (
                          <FaCheck className="mx-auto h-10 w-10" />
                        )}
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-1">
                        No transfers found
                      </h3>
                      <p className="text-sm text-gray-500">
                        {activeTab === "today"
                          ? "You don't have any transfers scheduled for today."
                          : activeTab === "upcoming"
                          ? "You don't have any upcoming transfers assigned to you."
                          : "You haven't completed any transfers yet."}
                      </p>
                    </div>
                  ) : (
                    filteredTransfers.map((transfer) => (
                      <motion.div
                        key={transfer._id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="p-6"
                      >
                        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4">
                          <div className="flex items-start space-x-4 mb-4 md:mb-0">
                            <div
                              className={`
                            p-3 rounded-full
                            ${
                              transfer.status === "confirmed"
                                ? "bg-blue-100"
                                : "bg-green-100"
                            }
                          `}
                            >
                              {getVehicleTypeIcon(transfer.carType)}
                            </div>

                            <div>
                              <h4 className="text-lg font-medium text-gray-900">
                                {transfer.name} {transfer.surname}
                              </h4>
                              <div className="flex flex-col sm:flex-row sm:items-center text-sm text-gray-500 gap-2 sm:gap-4">
                                <span className="flex items-center">
                                  <FaPhoneAlt className="mr-1 w-3 h-3" />
                                  {transfer.phone}
                                </span>
                                <span className="flex items-center">
                                  <FaEnvelope className="mr-1 w-3 h-3" />
                                  {transfer.email}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-2">
                            {transfer.status === "confirmed" && (
                              <button
                                onClick={() =>
                                  handleCompleteTransfer(transfer._id)
                                }
                                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none"
                              >
                                <FaCheck className="mr-2" />
                                Complete
                              </button>
                            )}

                            <button
                              onClick={() =>
                                handleAddNotes(
                                  transfer._id,
                                  transfer.driverNotes
                                )
                              }
                              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none"
                            >
                              <FaPencilAlt className="mr-2" />
                              {transfer.driverNotes
                                ? "Edit Notes"
                                : "Add Notes"}
                            </button>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                          {/* Transfer Details */}
                          <div className="bg-gray-50 rounded-lg p-4">
                            <h5 className="font-medium text-gray-700 text-sm mb-3">
                              Transfer Details
                            </h5>
                            <div className="space-y-2">
                              <div className="flex justify-between">
                                <span className="text-gray-500 text-sm">
                                  Type:
                                </span>
                                <span className="text-sm font-medium capitalize">
                                  {transfer.serviceType === "one-way"
                                    ? "One Way"
                                    : "Round Trip"}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500 text-sm">
                                  Vehicle:
                                </span>
                                <span className="text-sm font-medium capitalize">
                                  {transfer.carType}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500 text-sm">
                                  Passengers:
                                </span>
                                <span className="text-sm font-medium flex items-center">
                                  <FaUserFriends className="mr-1 w-3 h-3" />
                                  {transfer.persons}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500 text-sm">
                                  Luggage:
                                </span>
                                <span className="text-sm font-medium flex items-center">
                                  <FaSuitcase className="mr-1 w-3 h-3" />
                                  {transfer.luggage}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Date & Time */}
                          <div className="bg-gray-50 rounded-lg p-4">
                            <h5 className="font-medium text-gray-700 text-sm mb-3">
                              Schedule
                            </h5>
                            <div className="space-y-2">
                              {" "}
                              <div className="flex items-start">
                                <FaCalendarAlt className="text-blue-500 w-4 h-4 mr-2 mt-0.5" />
                                <div>
                                  <p className="text-sm font-medium">
                                    {moment(transfer.pickUpDate).format(
                                      "dddd, DD MMMM YYYY"
                                    )}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {transfer.pickupHours}:
                                    {transfer.pickupMinutes}
                                  </p>
                                </div>
                              </div>
                              {transfer.serviceType === "round-trip" &&
                                transfer.returnDate && (
                                  <div className="flex items-start">
                                    <FaCalendarAlt className="text-purple-500 w-4 h-4 mr-2 mt-0.5" />
                                    <div>
                                      <p className="text-sm font-medium">
                                        Return:{" "}
                                        {moment(transfer.returnDate).format(
                                          "dddd, DD MMMM YYYY"
                                        )}
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        {transfer.returnHours}:
                                        {transfer.returnMinutes}
                                      </p>
                                    </div>
                                  </div>
                                )}
                              {/* Countdown for upcoming transfers */}
                              {transfer.status === "confirmed" && (
                                <div className="mt-2 pt-2 border-t border-gray-200">
                                  <p className="text-xs text-gray-600">
                                    {moment(transfer.pickUpDate).isSame(
                                      new Date(),
                                      "day"
                                    )
                                      ? "Today"
                                      : moment(transfer.pickUpDate).fromNow()}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Locations */}
                          <div className="bg-gray-50 rounded-lg p-4">
                            <h5 className="font-medium text-gray-700 text-sm mb-3">
                              Route
                            </h5>
                            <div className="space-y-3">
                              <div className="flex">
                                <div className="mr-2 flex flex-col items-center">
                                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                                    <FaMapMarkerAlt className="text-green-600 w-3 h-3" />
                                  </div>
                                  <div className="w-0.5 h-full bg-gray-300 mt-1"></div>
                                </div>
                                <div>
                                  <p className="text-sm font-medium">Pickup</p>
                                  <p className="text-xs text-gray-600">
                                    {transfer.pickupLocation}
                                  </p>
                                </div>
                              </div>{" "}
                              <div className="flex">
                                <div className="mr-2">
                                  <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center">
                                    <FaMapMarkerAlt className="text-red-600 w-3 h-3" />
                                  </div>
                                  {transfer.serviceType === "round-trip" &&
                                    transfer.returnLocation && (
                                      <div className="w-0.5 h-full bg-gray-300 mt-1"></div>
                                    )}
                                </div>
                                <div>
                                  <p className="text-sm font-medium">
                                    Drop-off
                                  </p>
                                  <p className="text-xs text-gray-600">
                                    {transfer.dropoffLocation}
                                  </p>
                                </div>
                              </div>{" "}
                              {transfer.serviceType === "round-trip" &&
                                transfer.returnLocation && (
                                  <div className="flex">
                                    <div className="mr-2">
                                      <div className="w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center">
                                        <FaMapMarkerAlt className="text-purple-600 w-3 h-3" />
                                      </div>
                                    </div>
                                    <div>
                                      <p className="text-sm font-medium">
                                        Return Location
                                      </p>
                                      <p className="text-xs text-gray-600">
                                        {transfer.returnLocation}
                                      </p>
                                    </div>
                                  </div>
                                )}
                              {/* Navigation buttons */}{" "}
                              <div className="mt-4 grid grid-cols-2 gap-2">
                                <a
                                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                                    transfer.pickupLocation + ", Athens, Greece"
                                  )}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center justify-center px-2 py-1.5 bg-green-100 text-green-700 rounded-md text-xs font-medium hover:bg-green-200 transition-colors"
                                >
                                  <FaMapMarkerAlt className="mr-1" /> Pickup
                                  Directions
                                </a>
                                <a
                                  href={`https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(
                                    transfer.pickupLocation + ", Athens, Greece"
                                  )}&destination=${encodeURIComponent(
                                    transfer.dropoffLocation +
                                      ", Athens, Greece"
                                  )}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center justify-center px-2 py-1.5 bg-blue-100 text-blue-700 rounded-md text-xs font-medium hover:bg-blue-200 transition-colors"
                                >
                                  <FaRoute className="mr-1" /> Dropoff
                                  Directions
                                </a>
                                {transfer.serviceType === "round-trip" &&
                                  transfer.returnLocation && (
                                    <a
                                      href={`https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(
                                        transfer.dropoffLocation +
                                          ", Athens, Greece"
                                      )}&destination=${encodeURIComponent(
                                        transfer.returnLocation +
                                          ", Athens, Greece"
                                      )}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center justify-center px-2 py-1.5 bg-purple-100 text-purple-700 rounded-md text-xs font-medium hover:bg-purple-200 transition-colors col-span-2"
                                    >
                                      <FaExchangeAlt className="mr-1" /> Return
                                      Directions
                                    </a>
                                  )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Additional Services & Notes */}
                        <div className="mt-4">
                          {/* Additional Services */}
                          {(transfer.airportTicketId ||
                            transfer.atDisposal ||
                            transfer.helicopterTransfer) && (
                            <div className="mt-4 bg-blue-50 p-3 rounded-lg">
                              <h5 className="text-sm font-medium text-blue-800 mb-2">
                                Additional Services
                              </h5>
                              <div className="space-y-2">
                                {transfer.airportTicketId && (
                                  <div className="flex items-center text-sm">
                                    <span className="text-blue-800 mr-2">
                                      Flight/Ferry:
                                    </span>
                                    <span className="text-blue-900 font-medium">
                                      {transfer.airportTicketId}
                                    </span>
                                  </div>
                                )}

                                {transfer.atDisposal && (
                                  <div className="flex items-center text-sm">
                                    <span className="text-blue-800 mr-2">
                                      Driver at Disposal:
                                    </span>
                                    <span className="text-blue-900 font-medium">
                                      {transfer.disposalHours} hours
                                    </span>
                                  </div>
                                )}

                                {transfer.helicopterTransfer && (
                                  <div className="flex items-center text-sm">
                                    <span className="text-blue-800 mr-2">
                                      Helicopter Transfer:
                                    </span>
                                    <span className="text-blue-900 font-medium">
                                      Requested
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Driver Notes */}
                          {transfer.driverNotes && (
                            <div className="mt-4">
                              <div className="flex items-center text-sm font-medium text-gray-700 mb-2">
                                <FaCommentAlt className="mr-2 text-blue-600" />
                                Your Notes
                              </div>
                              <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
                                <p className="text-sm text-gray-800">
                                  {transfer.driverNotes}
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Customer Notes */}
                          {transfer.additionalInfo && (
                            <div className="mt-4">
                              <div className="flex items-center text-sm font-medium text-gray-700 mb-2">
                                <FaCommentAlt className="mr-2 text-gray-600" />
                                Customer Notes
                              </div>
                              <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                                <p className="text-sm text-gray-800">
                                  {transfer.additionalInfo}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default DriverDashboard;
